/**
 * Visitor tracking hook — Father House tracker port, noknok public API.
 *
 * Ships batched beacons to the Cloudflare Pages function at /api/track
 * (see functions/api/track.ts) via `navigator.sendBeacon` with a
 * `fetch(keepalive)` fallback:
 *
 *   - session bootstrap (device/browser/OS, viewport+screen, languages,
 *     timezone, hardware, connection, UTMs, referrer, device_signature)
 *   - pageview open + close (duration_ms, visibility-aware engagement_ms,
 *     scroll_max_pct)
 *   - events (the legacy trackEvent/trackClick/trackSectionView/trackScroll
 *     API is preserved and mapped onto the wire format)
 *   - heartbeat every 30s (visible + focused only) to keep last_seen_at fresh
 *
 * The old `track-visitor-event` Supabase edge function is NOT called anymore.
 * The `notify-agents` pings (live-chat agent presence/departed notifications)
 * are kept — they are a chat feature, not analytics storage.
 *
 * Identity: keeps the EXACT storage keys the chat linkage depends on —
 * localStorage `noknok_visitor_id` ('v_...') and sessionStorage
 * `noknok_session_id` ('s_...').
 *
 * Consent: gated on localStorage `noknok_tracking_consent` === 'true'.
 * No consent → no /api/track beacons. Events raised pre-consent are buffered
 * in memory and flushed when consent is granted (same as before).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// ---------------------------------------------------------------- constants

const ENDPOINT = '/api/track';
const FLUSH_INTERVAL_MS = 5_000;
const BUFFER_LIMIT = 25;
const HEARTBEAT_MS = 30_000;

// null = the noknok.pro platform site itself. Structured so a per-business
// site could pass its business uuid here later.
const BUSINESS_ID: string | null = null;

// Storage keys — DO NOT change: ChatWidget + chat_sessions linkage share them.
const VISITOR_ID_KEY = 'noknok_visitor_id';
const SESSION_ID_KEY = 'noknok_session_id';
const CONSENT_KEY = 'noknok_tracking_consent';
const NEWSLETTER_DISMISSED_KEY = 'noknok_newsletter_dismissed';
const NEWSLETTER_SESSION_KEY = 'noknok_newsletter_session_dismissed';

// ---------------------------------------------------------------- ids

const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

// Get or create visitor ID (persistent across sessions)
const getVisitorId = (): string => {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
        visitorId = `v_${generateId()}`;
        localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }
    return visitorId;
};

// Get or create session ID (unique per browser session)
const getSessionId = (): string => {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
        sessionId = `s_${generateId()}`;
        sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
};

const newRowId = (): string => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    // Fallback (very old browsers) — non-cryptographic but acceptable.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

// ---------------------------------------------------------------- wire types

type DeviceKind = 'mobile' | 'tablet' | 'desktop';

type SessBootstrap = {
    started_at: string;
    user_agent: string;
    device_kind: DeviceKind;
    browser: string;
    os: string;
    is_pwa: boolean;
    viewport_w: number;
    viewport_h: number;
    referrer: string;
    landing_path: string;
    utm: Record<string, string | undefined>;
    // Enrichment
    screen_w?: number;
    screen_h?: number;
    color_depth?: number;
    language?: string;
    languages?: string[];
    timezone?: string;
    hardware_concurrency?: number;
    device_memory_gb?: number;
    prefers_dark?: boolean;
    touch_points?: number;
    connection_type?: string;
    connection_downlink_mbps?: number;
    connection_rtt_ms?: number;
    connection_save_data?: boolean;
    device_signature?: string;
};

type PageviewWire = {
    id: string;
    path: string;
    query: string;
    title: string;
    referrer_path: string;
    started_at: string;
    duration_ms?: number;
    engagement_ms?: number;
    scroll_max_pct?: number;
};

type PageviewUpdateWire = {
    id: string;
    duration_ms: number;
    engagement_ms: number;
    scroll_max_pct: number;
    ended_at: string;
};

type EventWire = {
    id: string;
    pageview_id?: string | null;
    kind: string;
    name?: string;
    target_text?: string;
    target_selector?: string;
    target_href?: string;
    path: string;
    meta?: Record<string, unknown>;
    created_at: string;
};

type TrackBatch = {
    v: 1;
    vid: string;
    sid: string;
    bid?: string | null;
    uid?: string | null;
    sess?: SessBootstrap;
    pvs?: PageviewWire[];
    pvUpdates?: PageviewUpdateWire[];
    evs?: EventWire[];
    hb?: true;
};

// Legacy event shape — the hook's public trackEvent API (unchanged).
interface VisitorEvent {
    event_type: string;
    page_path?: string;
    element_id?: string;
    element_text?: string;
    element_type?: string;
    duration_ms?: number;
    scroll_depth_percent?: number;
    metadata?: Record<string, unknown>;
    timestamp: string;
}

// ---------------------------------------------------------------- detection

const detectDevice = (): DeviceKind => {
    if (typeof navigator === 'undefined') return 'desktop';
    const ua = navigator.userAgent || '';
    const w = typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 0;
    const isTablet =
        /iPad/i.test(ua) ||
        (/(Android)/i.test(ua) && !/(Mobile)/i.test(ua)) ||
        (w >= 600 && w <= 1100 && /(Tablet)/i.test(ua));
    if (isTablet) return 'tablet';
    const isMobile =
        /(iPhone|iPod|Android.*Mobile|webOS|BlackBerry|Mobile)/i.test(ua) || w < 600;
    return isMobile ? 'mobile' : 'desktop';
};

const detectBrowser = (): string => {
    if (typeof navigator === 'undefined') return 'unknown';
    const ua = navigator.userAgent;
    if (/Edg\//.test(ua)) return 'Edge';
    if (/OPR\//.test(ua)) return 'Opera';
    if (/Firefox\//.test(ua)) return 'Firefox';
    if (/Chrome\//.test(ua)) return 'Chrome';
    if (/Safari\//.test(ua)) return 'Safari';
    return 'unknown';
};

const detectOS = (): string => {
    if (typeof navigator === 'undefined') return 'unknown';
    const ua = navigator.userAgent;
    if (/Windows NT/i.test(ua)) return 'Windows';
    if (/Mac OS X/i.test(ua)) return 'macOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return 'unknown';
};

const detectPWA = (): boolean => {
    if (typeof window === 'undefined') return false;
    const mql = window.matchMedia?.('(display-mode: standalone)');
    if (mql?.matches) return true;
    // iOS Safari pinned to home screen
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window.navigator as any)?.standalone === true) return true;
    return false;
};

const parseUTM = (search: string): Record<string, string> => {
    const out: Record<string, string> = {};
    if (!search) return out;
    try {
        const sp = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
        for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
            const v = sp.get(k);
            if (v) out[k] = v.slice(0, 128);
        }
    } catch {
        // ignore
    }
    return out;
};

type NavConnection = {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
};

type DeviceSignals = Omit<SessBootstrap,
    'started_at' | 'user_agent' | 'device_kind' | 'browser' | 'os' | 'is_pwa' |
    'viewport_w' | 'viewport_h' | 'referrer' | 'landing_path' | 'utm'>;

const collectDeviceSignals = (): DeviceSignals => {
    if (typeof window === 'undefined') return {};
    const s: DeviceSignals = {};

    if (window.screen) {
        s.screen_w = window.screen.width || undefined;
        s.screen_h = window.screen.height || undefined;
        s.color_depth = window.screen.colorDepth || undefined;
    }
    if (typeof navigator !== 'undefined') {
        s.language = navigator.language || undefined;
        s.languages = navigator.languages ? Array.from(navigator.languages).slice(0, 8) : undefined;
        s.hardware_concurrency = navigator.hardwareConcurrency || undefined;
        s.touch_points = navigator.maxTouchPoints ?? undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const devMem = (navigator as any).deviceMemory;
        if (typeof devMem === 'number') s.device_memory_gb = devMem;
        const conn = (navigator as unknown as { connection?: NavConnection }).connection;
        if (conn) {
            s.connection_type = conn.effectiveType;
            s.connection_downlink_mbps = conn.downlink;
            s.connection_rtt_ms = conn.rtt;
            s.connection_save_data = conn.saveData;
        }
    }
    try {
        s.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        // ignore
    }
    try {
        s.prefers_dark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    } catch {
        // ignore
    }
    s.device_signature = deviceSignature(s);
    return s;
};

/**
 * A 32-bit FNV-1a hash of stable device attributes — recognizes the same
 * physical device across storage clears. A badge, not a fingerprint.
 */
const deviceSignature = (s: DeviceSignals): string => {
    const parts: string[] = [
        String(s.screen_w ?? ''),
        String(s.screen_h ?? ''),
        String(s.color_depth ?? ''),
        s.timezone ?? '',
        s.language ?? '',
        String(s.hardware_concurrency ?? ''),
        String(s.device_memory_gb ?? ''),
        String(s.touch_points ?? ''),
        typeof navigator !== 'undefined' ? navigator.platform || '' : '',
    ];
    const input = parts.join('|');
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
};

// ---------------------------------------------------------------- engagement

/**
 * Per-pageview engagement + scroll-depth tracking.
 *
 * Engagement = time the page is in the foreground AND the user interacted
 * within the last 30s. Pauses when hidden or idle. 1s setTimeout tick.
 */
const IDLE_MS = 30_000;
const TICK_MS = 1_000;

type EngagementSnapshot = { engagementMs: number; scrollMaxPct: number };

class EngagementTimer {
    private engagementMs = 0;
    private scrollMaxPct = 0;
    private lastInteractionAt = Date.now();
    private active = false;
    private timer: number | null = null;
    private cleanup: Array<() => void> = [];

    constructor() {
        this.start();
    }

    private tick = () => {
        if (this.active && document.visibilityState === 'visible') {
            const idleFor = Date.now() - this.lastInteractionAt;
            if (idleFor < IDLE_MS) this.engagementMs += TICK_MS;
        }
        this.timer = window.setTimeout(this.tick, TICK_MS);
    };

    private bump = () => {
        this.lastInteractionAt = Date.now();
    };

    private onVis = () => {
        this.active = document.visibilityState === 'visible';
        if (this.active) this.bump();
    };

    private onScroll = () => {
        this.bump();
        const doc = document.documentElement;
        const total = doc.scrollHeight - window.innerHeight;
        if (total <= 0) {
            this.scrollMaxPct = 100;
            return;
        }
        const pct = Math.min(100, Math.max(0,
            ((doc.scrollTop || window.scrollY) + window.innerHeight) / doc.scrollHeight * 100));
        const rounded = Math.round(pct);
        if (rounded > this.scrollMaxPct) this.scrollMaxPct = rounded;
    };

    start() {
        if (typeof document === 'undefined') return;
        this.active = document.visibilityState === 'visible';
        this.lastInteractionAt = Date.now();
        this.onScroll(); // initial snapshot (above-the-fold = 100% on short pages)

        const opts: AddEventListenerOptions = { passive: true };
        const reg = (
            target: Document | Window,
            ev: string,
            fn: EventListener,
            o: AddEventListenerOptions = opts,
        ) => {
            target.addEventListener(ev, fn, o);
            this.cleanup.push(() => target.removeEventListener(ev, fn, o));
        };

        reg(document, 'visibilitychange', this.onVis as EventListener, {});
        reg(window, 'scroll', this.onScroll as EventListener);
        reg(window, 'pointerdown', this.bump as EventListener);
        reg(window, 'pointermove', this.bump as EventListener);
        reg(window, 'keydown', this.bump as EventListener);
        reg(window, 'touchstart', this.bump as EventListener);

        this.timer = window.setTimeout(this.tick, TICK_MS);
    }

    snapshot(): EngagementSnapshot {
        return { engagementMs: this.engagementMs, scrollMaxPct: this.scrollMaxPct };
    }

    stop(): EngagementSnapshot {
        if (this.timer != null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        for (const c of this.cleanup) c();
        this.cleanup = [];
        return this.snapshot();
    }
}

// ---------------------------------------------------------------- tracker singleton

type TrackerState = {
    vid: string;
    sid: string;
    sessionStartedAt: string;
    bootstrappedThisSession: boolean;
    currentPath: string;
    currentPv: { id: string; startedAt: string; timer: EngagementTimer } | null;
    pendingPvs: PageviewWire[];
    pendingPvUpdates: PageviewUpdateWire[];
    pendingEvents: EventWire[];
    lastHeartbeatAt: number;
    windowHasFocus: boolean;
    flushTimer: number | null;
    heartbeatTimer: number | null;
    listenerCleanup: Array<() => void>;
};

let trk: TrackerState | null = null;
let running = false;

const ensureTrackerState = (): TrackerState => {
    if (trk) return trk;
    trk = {
        vid: getVisitorId(),
        sid: getSessionId(),
        sessionStartedAt: new Date().toISOString(),
        // Always send the session bootstrap on the first flush of a page-load.
        // The server upserts on session_key so it's idempotent — keeps
        // enrichment (screen, geo, timezone, ...) fresh for resumed sessions.
        bootstrappedThisSession: false,
        currentPath: '',
        currentPv: null,
        pendingPvs: [],
        pendingPvUpdates: [],
        pendingEvents: [],
        lastHeartbeatAt: 0,
        windowHasFocus: typeof document !== 'undefined' ? document.hasFocus() : true,
        flushTimer: null,
        heartbeatTimer: null,
        listenerCleanup: [],
    };
    return trk;
};

const buildBootstrap = (s: TrackerState, initialPath: string): SessBootstrap => ({
    started_at: s.sessionStartedAt,
    user_agent: navigator.userAgent || '',
    device_kind: detectDevice(),
    browser: detectBrowser(),
    os: detectOS(),
    is_pwa: detectPWA(),
    viewport_w: window.innerWidth || 0,
    viewport_h: window.innerHeight || 0,
    referrer: document.referrer || '',
    landing_path: initialPath,
    utm: parseUTM(window.location.search),
    ...collectDeviceSignals(),
});

const sendBatch = (batch: TrackBatch): void => {
    const body = JSON.stringify(batch);
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            const blob = new Blob([body], { type: 'application/json' });
            if (navigator.sendBeacon(ENDPOINT, blob)) return;
        }
    } catch {
        // fall through to fetch
    }
    try {
        void fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body,
            keepalive: true,
        }).catch(() => undefined);
    } catch {
        // ignore — analytics must never break the page
    }
};

const trackerFlush = (force = false): void => {
    const s = trk;
    if (!s || !running) return;

    const hasPayload =
        s.pendingPvs.length > 0 ||
        s.pendingPvUpdates.length > 0 ||
        s.pendingEvents.length > 0;
    const needBootstrap = !s.bootstrappedThisSession;
    if (!hasPayload && !needBootstrap && !force) return;

    const batch: TrackBatch = { v: 1, vid: s.vid, sid: s.sid, bid: BUSINESS_ID };
    if (needBootstrap) {
        batch.sess = buildBootstrap(s, s.currentPath || window.location.pathname);
        s.bootstrappedThisSession = true;
    }
    if (s.pendingPvs.length > 0) {
        batch.pvs = s.pendingPvs;
        s.pendingPvs = [];
    }
    if (s.pendingPvUpdates.length > 0) {
        batch.pvUpdates = s.pendingPvUpdates;
        s.pendingPvUpdates = [];
    }
    if (s.pendingEvents.length > 0) {
        batch.evs = s.pendingEvents;
        s.pendingEvents = [];
    }
    s.lastHeartbeatAt = Date.now();
    sendBatch(batch);
};

const trackerHeartbeat = (): void => {
    const s = trk;
    if (!s || !running) return;
    // Only heartbeat while the page is visible AND the window is focused —
    // same rule the old hook used, so "active" in Live views means active.
    if (document.hidden || !s.windowHasFocus) return;
    const now = Date.now();
    if (now - s.lastHeartbeatAt < HEARTBEAT_MS) return;
    s.lastHeartbeatAt = now;
    sendBatch({ v: 1, vid: s.vid, sid: s.sid, bid: BUSINESS_ID, hb: true });
};

const maybeFlushOnBufferFull = (): void => {
    const s = trk;
    if (!s) return;
    const total = s.pendingPvs.length + s.pendingPvUpdates.length + s.pendingEvents.length;
    if (total >= BUFFER_LIMIT) trackerFlush();
};

const closeCurrentPv = (s: TrackerState, finalStop: boolean): void => {
    if (!s.currentPv) return;
    const snap = finalStop ? s.currentPv.timer.stop() : s.currentPv.timer.snapshot();
    s.pendingPvUpdates.push({
        id: s.currentPv.id,
        duration_ms: Date.now() - new Date(s.currentPv.startedAt).getTime(),
        engagement_ms: snap.engagementMs,
        scroll_max_pct: snap.scrollMaxPct,
        ended_at: new Date().toISOString(),
    });
    if (finalStop) s.currentPv = null;
};

const trackerPageview = (path: string, title: string): void => {
    if (!trk || !running) return;
    const s = trk;
    // Ignore repeat notifications for the page we're already on (the hook's
    // route-change effect + start() can both fire for the initial path).
    if (s.currentPv && s.currentPath === path) return;

    // Close the previous pageview (if any) with final engagement/scroll.
    closeCurrentPv(s, true);

    const referrerPath = s.currentPath || document.referrer || '';
    s.currentPath = path;

    const pv: PageviewWire = {
        id: newRowId(),
        path,
        query: window.location.search || '',
        title: (title || document.title || '').slice(0, 256),
        referrer_path: referrerPath,
        started_at: new Date().toISOString(),
        engagement_ms: 0,
        scroll_max_pct: 0,
    };
    s.currentPv = { id: pv.id, startedAt: pv.started_at, timer: new EngagementTimer() };
    s.pendingPvs.push(pv);

    // Flush right away on every pageview so Live/Traffic views are near-real-time.
    trackerFlush(false);
};

const trackerEvent = (e: Omit<VisitorEvent, 'timestamp'>): void => {
    if (!trk || !running) return;
    const s = trk;
    const meta: Record<string, unknown> = { ...(e.metadata ?? {}) };
    if (e.element_type) meta.element_type = e.element_type;
    if (typeof e.duration_ms === 'number') meta.duration_ms = e.duration_ms;
    if (typeof e.scroll_depth_percent === 'number') meta.scroll_depth_percent = e.scroll_depth_percent;

    s.pendingEvents.push({
        id: newRowId(),
        pageview_id: s.currentPv?.id ?? null,
        kind: e.event_type,
        name: e.element_id || undefined,
        target_text: e.element_text || undefined,
        path: e.page_path || s.currentPath || window.location.pathname,
        meta,
        created_at: new Date().toISOString(),
    });
    maybeFlushOnBufferFull();
};

const startTracker = (initialPath: string, initialTitle: string): void => {
    if (running) return;
    const s = ensureTrackerState();
    s.currentPath = s.currentPath || initialPath;
    running = true;

    // Initial pageview, then flush immediately so the session row exists.
    trackerPageview(initialPath, initialTitle);
    trackerFlush(true);

    s.flushTimer = window.setInterval(() => trackerFlush(false), FLUSH_INTERVAL_MS);
    s.heartbeatTimer = window.setInterval(trackerHeartbeat, HEARTBEAT_MS);

    const reg = (target: Document | Window, ev: string, fn: EventListener, o?: AddEventListenerOptions) => {
        target.addEventListener(ev, fn, o);
        s.listenerCleanup.push(() => target.removeEventListener(ev, fn, o));
    };

    // End-of-page flush: close the current pv with final engagement/scroll.
    reg(window, 'pagehide', (() => {
        if (trk?.currentPv) {
            const snap = trk.currentPv.timer.snapshot();
            trk.pendingPvUpdates.push({
                id: trk.currentPv.id,
                duration_ms: Date.now() - new Date(trk.currentPv.startedAt).getTime(),
                engagement_ms: snap.engagementMs,
                scroll_max_pct: snap.scrollMaxPct,
                ended_at: new Date().toISOString(),
            });
        }
        trackerFlush(true);
    }) as EventListener, { capture: true });

    reg(document, 'visibilitychange', (() => {
        if (document.visibilityState === 'hidden') trackerFlush(false);
        else trackerHeartbeat();
    }) as EventListener);

    reg(window, 'focus', (() => {
        if (trk) trk.windowHasFocus = true;
        trackerHeartbeat();
    }) as EventListener);

    reg(window, 'blur', (() => {
        if (trk) trk.windowHasFocus = false;
    }) as EventListener);
};

const stopTracker = (): void => {
    if (!running) return;
    const s = trk;
    if (s) {
        closeCurrentPv(s, true);
        trackerFlush(true);
    }
    running = false;
    if (s) {
        if (s.flushTimer != null) {
            window.clearInterval(s.flushTimer);
            s.flushTimer = null;
        }
        if (s.heartbeatTimer != null) {
            window.clearInterval(s.heartbeatTimer);
            s.heartbeatTimer = null;
        }
        for (const c of s.listenerCleanup) c();
        s.listenerCleanup = [];
    }
};

// ---------------------------------------------------------------- hook

interface UseVisitorTrackingReturn {
    visitorId: string;
    sessionId: string;
    hasConsent: boolean;
    setConsent: (consent: boolean) => void;
    trackEvent: (event: Omit<VisitorEvent, 'timestamp'>) => void;
    trackPageView: (pagePath?: string) => void;
    trackClick: (elementId: string, elementText?: string, metadata?: Record<string, unknown>) => void;
    trackSectionView: (sectionId: string, sectionTitle?: string, durationMs?: number) => void;
    trackScroll: (scrollPercent: number) => void;
    isNewsletterDismissed: () => boolean;
    dismissNewsletter: (permanent: boolean) => void;
    captureContact: (email?: string, phone?: string, name?: string) => Promise<void>;
    flushEvents: () => Promise<void>;
}

export function useVisitorTracking(): UseVisitorTrackingReturn {
    const location = useLocation();
    const [hasConsent, setHasConsent] = useState(() => {
        return localStorage.getItem(CONSENT_KEY) === 'true';
    });

    const visitorId = useRef(getVisitorId());
    const sessionId = useRef(getSessionId());
    // Events raised before consent is granted are buffered in memory and
    // replayed into the tracker if/when the user accepts.
    const preConsentQueue = useRef<Array<Omit<VisitorEvent, 'timestamp'>>>([]);
    const presenceSentRef = useRef(false);

    // AGENT PRESENCE PING — live-chat feature, not analytics. Fires once per
    // session on mount, no consent required (no personal data stored; it only
    // pushes a "someone is here" notification to agents).
    useEffect(() => {
        if (presenceSentRef.current) return;

        // sessionStorage guard prevents StrictMode double-mount duplicates.
        const notificationKey = `noknok_notified_${sessionId.current}`;
        if (sessionStorage.getItem(notificationKey)) return;

        presenceSentRef.current = true;
        sessionStorage.setItem(notificationKey, 'true');

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        if (!supabaseUrl) return;

        const formatPagePath = (path: string) => (path === '/' ? '/home' : path);

        fetch(`${supabaseUrl}/functions/v1/notify-agents`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                type: 'new_visitor',
                visitor_id: visitorId.current,
                current_page: formatPagePath(window.location.pathname),
            }),
        }).catch(() => { }); // Silent fail
    }, []);

    // AGENT DEPARTED PING — mirror of the presence ping, on page close.
    useEffect(() => {
        const handleUnload = () => {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            if (!supabaseUrl) return;
            navigator.sendBeacon(
                `${supabaseUrl}/functions/v1/notify-agents`,
                JSON.stringify({
                    type: 'visitor_departed',
                    visitor_id: visitorId.current,
                    current_page: trk?.currentPath || window.location.pathname,
                }),
            );
        };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, []);

    // Start the tracker once consent exists (persisted or newly granted).
    useEffect(() => {
        if (hasConsent && !running) {
            startTracker(location.pathname, document.title);
        }
        // Intentionally no stop() on unmount: the tracker is a page-level
        // singleton (pagehide handles the final flush).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasConsent]);

    // Auto-track SPA route changes (trackerPageview de-dupes the initial path).
    useEffect(() => {
        if (hasConsent && running) {
            trackerPageview(location.pathname, document.title);
        }
    }, [location.pathname, hasConsent]);

    // Set consent — and immediately replay any buffered events.
    const setConsent = useCallback((consent: boolean) => {
        setHasConsent(consent);
        localStorage.setItem(CONSENT_KEY, consent ? 'true' : 'false');

        if (consent) {
            startTracker(window.location.pathname, document.title);
            if (preConsentQueue.current.length > 0) {
                const buffered = [...preConsentQueue.current];
                preConsentQueue.current = [];
                for (const e of buffered) trackerEvent(e);
            }
            trackerFlush(true);
        } else {
            stopTracker();
        }
    }, []);

    // Track generic event — queue pre-consent, send post-consent.
    const trackEvent = useCallback((event: Omit<VisitorEvent, 'timestamp'>) => {
        if (running) {
            trackerEvent(event);
        } else {
            preConsentQueue.current.push(event);
        }
    }, []);

    // Manual page view (route changes are auto-tracked).
    const trackPageView = useCallback((pagePath?: string) => {
        trackerPageview(pagePath || location.pathname, document.title);
    }, [location.pathname]);

    // Track click
    const trackClick = useCallback((
        elementId: string,
        elementText?: string,
        metadata?: Record<string, unknown>,
    ) => {
        trackEvent({
            event_type: 'button_click',
            page_path: location.pathname,
            element_id: elementId,
            element_text: elementText,
            element_type: 'button',
            metadata,
        });
    }, [location.pathname, trackEvent]);

    // Track section view
    const trackSectionView = useCallback((
        sectionId: string,
        sectionTitle?: string,
        durationMs?: number,
    ) => {
        trackEvent({
            event_type: 'section_view',
            page_path: location.pathname,
            element_id: sectionId,
            element_text: sectionTitle,
            element_type: 'section',
            duration_ms: durationMs,
        });
    }, [location.pathname, trackEvent]);

    // Track scroll depth (the pageview's scroll_max_pct is captured
    // automatically; this keeps the explicit event API working too).
    const trackScroll = useCallback((scrollPercent: number) => {
        trackEvent({
            event_type: 'scroll',
            page_path: location.pathname,
            scroll_depth_percent: Math.round(scrollPercent),
        });
    }, [location.pathname, trackEvent]);

    // Newsletter dismissal (unchanged)
    const isNewsletterDismissed = useCallback(() => {
        return (
            localStorage.getItem(NEWSLETTER_DISMISSED_KEY) === 'true' ||
            sessionStorage.getItem(NEWSLETTER_SESSION_KEY) === 'true'
        );
    }, []);

    const dismissNewsletter = useCallback((permanent: boolean) => {
        if (permanent) {
            localStorage.setItem(NEWSLETTER_DISMISSED_KEY, 'true');
        } else {
            sessionStorage.setItem(NEWSLETTER_SESSION_KEY, 'true');
        }
    }, []);

    // Capture contact info and link to visitor (unchanged — separate edge fn)
    const captureContact = useCallback(async (
        email?: string,
        phone?: string,
        name?: string,
    ) => {
        if (!email && !phone) return;

        try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            if (!supabaseUrl) return;

            await fetch(`${supabaseUrl}/functions/v1/capture-lead-contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    visitor_id: visitorId.current,
                    email,
                    phone,
                    name,
                }),
            });

            // Auto-dismiss newsletter on successful capture
            dismissNewsletter(true);
        } catch (err) {
            console.error('Failed to capture contact:', err);
        }
    }, [dismissNewsletter]);

    // Force-flush pending analytics to /api/track.
    const flushEvents = useCallback(async () => {
        trackerFlush(true);
    }, []);

    // Listen for section view events from TrackedSection component
    useEffect(() => {
        const handleSectionView = (e: CustomEvent<{ sectionId: string; sectionTitle?: string; durationMs: number }>) => {
            if (hasConsent && e.detail) {
                trackSectionView(e.detail.sectionId, e.detail.sectionTitle, e.detail.durationMs);
            }
        };

        window.addEventListener('noknok:section_view', handleSectionView as EventListener);
        return () => window.removeEventListener('noknok:section_view', handleSectionView as EventListener);
    }, [hasConsent, trackSectionView]);

    return {
        visitorId: visitorId.current,
        sessionId: sessionId.current,
        hasConsent,
        setConsent,
        trackEvent,
        trackPageView,
        trackClick,
        trackSectionView,
        trackScroll,
        isNewsletterDismissed,
        dismissNewsletter,
        captureContact,
        flushEvents,
    };
}
